import { FormRow } from '@cad-arc';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { OtherItemsBaseKey } from '../types';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormCheckbox } from '../../../../forms/integrated-form-controls/form-checkbox';
import { FormTextarea } from '../../../../forms/integrated-form-controls/form-text-area';
import { FormNumberInput } from '../../../../forms/integrated-form-controls/form-number-input';
import { getUniversalSearchLabel } from '../../../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features';
import { useIsClearedEvent } from '../../use-is-cleared-event';
import { useIsDexEnabled } from '../../../../data-exchange/use-dex-access-guard';

export function EventPanelFormOtherItemsPropertyInputs(props: {
    form: UseFormReturn<EventPanelFormType>;
    index: number;
    baseKey: OtherItemsBaseKey;
    eventPanelOptions: EventPanelOptions;
    showExtraFields: boolean;
    isPersisted: boolean;
}) {
    const {
        form: eventForm,
        index,
        baseKey,
        showExtraFields,
        eventPanelOptions,
        isPersisted,
    } = props;

    const { dexNextEnabled } = useEnabledFeatures();
    const { isDexEnabled } = useIsDexEnabled();
    const isClearedEvent = useIsClearedEvent();

    React.useEffect(
        function disableQueryInDexWhenDexIsBlocked() {
            if (!isDexEnabled) {
                eventForm.setValue(`${baseKey}.${index}.queryInDex`, false);
            }
        },
        [baseKey, eventForm, index, isDexEnabled]
    );

    return (
        <>
            <FormRow>
                <FormInput
                    control={eventForm.control}
                    label="Serial #"
                    name={`${baseKey}.${index}.serialNumber`}
                    asEditable={!!isPersisted}
                    length="sm"
                    eventNotificationName="OTHER_ITEM_SERIAL_NUMBER"
                />
                <FormSelect
                    length="sm"
                    control={eventForm.control}
                    label="Item Category"
                    options={eventPanelOptions.itemCategoryOptions}
                    name={`${props.baseKey}.${index}.itemCategory`}
                    asEditable={!!isPersisted}
                    eventNotificationName="OTHER_ITEM_ITEM_CATEGORY"
                />
                <FormSelect
                    length="md"
                    control={eventForm.control}
                    label="Primary Color"
                    options={eventPanelOptions.itemColorOptions}
                    name={`${props.baseKey}.${index}.color`}
                    asEditable={!!isPersisted}
                    eventNotificationName="OTHER_ITEM_COLOR"
                />
            </FormRow>
            <FormRow>
                <FormNumberInput
                    numberInputProps={{ precision: 0 }}
                    length="md"
                    control={eventForm.control}
                    label="Quantity"
                    name={`${baseKey}.${index}.quantity`}
                    asEditable={!!isPersisted}
                    eventNotificationName="OTHER_ITEM_QUANTITY"
                />
                <FormInput
                    control={eventForm.control}
                    label="Size"
                    name={`${props.baseKey}.${index}.size`}
                    length="sm"
                    asEditable={!!isPersisted}
                    eventNotificationName="OTHER_ITEM_SIZE"
                />
            </FormRow>
            <FormTextarea
                control={eventForm.control}
                label="Description"
                name={`${props.baseKey}.${index}.description`}
                length="md"
                asEditable={!!isPersisted}
                eventNotificationName="OTHER_ITEM_DESCRIPTION"
            />
            {!isPersisted && isDexEnabled && (
                <FormRow>
                    <FormCheckbox
                        label={`Query in ${getUniversalSearchLabel(dexNextEnabled)}`}
                        name={`${baseKey}.${index}.queryInDex`}
                        control={props.form.control}
                        asEditable={false}
                        length="sm"
                        size="sm"
                        isDisabled={isClearedEvent}
                        checkboxProps={
                            isClearedEvent ? { sx: { cursor: 'not-allowed' } } : undefined
                        }
                    />
                </FormRow>
            )}
            {showExtraFields ? (
                <>
                    <FormRow>
                        <FormInput
                            control={eventForm.control}
                            label="Make/Brand"
                            name={`${props.baseKey}.${index}.make`}
                            length="md"
                            asEditable={!!isPersisted}
                            eventNotificationName="OTHER_ITEM_MAKE"
                        />
                        <FormInput
                            control={eventForm.control}
                            label="Model"
                            name={`${props.baseKey}.${index}.model`}
                            length="md"
                            asEditable={!!isPersisted}
                            eventNotificationName="OTHER_ITEM_MODEL"
                        />
                    </FormRow>
                    <FormRow>
                        <FormSelect
                            control={eventForm.control}
                            label="Identifier Type"
                            name={`${props.baseKey}.${index}.identifierType`}
                            length="md"
                            options={eventPanelOptions.itemIdentifierOptions}
                            asEditable={!!isPersisted}
                            eventNotificationName="OTHER_ITEM_IDENTIFIER_TYPE"
                        />
                        <FormInput
                            control={eventForm.control}
                            label="Identifier"
                            name={`${props.baseKey}.${index}.identifier`}
                            length="md"
                            asEditable={!!isPersisted}
                            eventNotificationName="OTHER_ITEM_IDENTIFIER"
                        />
                    </FormRow>
                </>
            ) : undefined}
        </>
    );
}
